import { Injectable } from '@angular/core';
import { GlobalService } from './global.service';
import { ApiService } from './api.service';
import * as dayjs from 'dayjs';
import { ApplyUseProjectDates } from '../services/task-project-dates.util';
import { ClientStepTypes } from '../components/steps/StepTypes';

export interface RepeatRules {
  version: '1',
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'semi-monthly' | 'twice-monthly' | 'quarterly' | 'semi-annually';
  interval: number;                 // every N units
  pattern: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'semi-monthly' | 'twice-monthly' | 'quarterly' | 'semi-annually'; // mirrors frequency
  weekdays: boolean[];            // length 7, Sunday=0 ... Saturday=6 (assumption)
  months: boolean[];
  monthly_any: boolean;
  monthly_type: 'day' | 'weekday' | 'special';  // 'day' = day-of-month, 'weekday' = ordinal weekday
  monthly_day: number;              // 1..31 (used when monthly_type='day')
  monthly_ordinal: string;          // '1'|'2'|'3'|'4'|'5'|'last'
  monthly_weekday: string;          // '0'..'6' (Sun..Sat)
  monthly_special: 'start' | 'end' | 'none'; // if you need alignment with original project dates
  yearly_month: number;             // 1..12
  yearly_day: number;               // 1..31
  yearly_use_ordinal: boolean;
  yearly_ordinal: string;           // '1'|'2'|'3'|'4'|'5'|'last'
  yearly_weekday: string;           // '0'..'6'
  yearly_ordinal_month: number;     // 1..12
  custom: string;                   // (unused here)
  end_type: 'never' | 'on' | 'after'; // on=end_date, after=end_after
  end_date: string | null;          // ISO date when end_type='on'
  end_after: number;                // number of occurrences when end_type='after'
  // Semi-monthly settings (pick any two dates in a month)
  semi_monthly_first_day: number;   // 1-31, first date in month
  semi_monthly_second_day: number;  // 1-31, second date in month

  repeat_start_date: string;                // e.g. '2026-01-01'
  internal_end_offset_unit: 'day' | 'week' | 'month';
  internal_start_offset_unit: 'day' | 'week' | 'month';
  external_start_offset_unit: 'day' | 'week' | 'month';
  external_end_offset_unit: 'day' | 'week' | 'month';
  period_start_offset_unit: 'day' | 'week' | 'month';
  period_end_offset_unit: 'day' | 'week' | 'month';

  relative_date_type: string;               // e.g. "project_repeat_date"
  internal_start_offset: number;
  internal_end_offset: number;
  external_start_offset: number;
  external_end_offset: number;
  period_start_offset: number,
  period_end_offset: number,

  custom_start_date?: boolean;

  internal_start_date_before?: boolean;
  internal_end_date_before?: boolean;
  external_start_date_before?: boolean;
  external_end_date_before?: boolean;
  period_start_date_before?: boolean;
  period_end_date_before?: boolean;

  internal_start_offset_type?: 'start_of_month' | 'end_of_month' | 'calculated_date';
  internal_end_offset_type?: 'start_of_month' | 'end_of_month' | 'calculated_date';
  external_start_offset_type?: 'start_of_month' | 'end_of_month' | 'calculated_date';
  external_end_offset_type?: 'start_of_month' | 'end_of_month' | 'calculated_date';
  period_start_offset_type?: 'start_of_month' | 'end_of_month' | 'calculated_date';
  period_end_offset_type?: 'start_of_month' | 'end_of_month' | 'calculated_date';

};

@Injectable({
  providedIn: 'root'
})
export class ProjectsService {

  constructor(public globalService: GlobalService, private apiService: ApiService) {
    this.RegisterChunkedLoadingDebugHooks();
  }

  /**
   * Exposes window.enableChunkedLoading() / window.disableChunkedLoading() in the Chrome
   * console so chunked project/task loading can be toggled off to compare against loading the
   * whole date range in a single request while debugging. Defaults to enabled: with the O(T^2)
   * task/project cloning blowup fixed, chunked (month-by-month) generation is safe and lets the
   * projects/tasks tables paint instantly with the first month while the rest streams in behind
   * the "still loading in the background" indicator.
   */
  private RegisterChunkedLoadingDebugHooks() {
    if (typeof window === 'undefined') return;
    const win = window as any;
    if (win.chunkedLoadingEnabled === undefined) win.chunkedLoadingEnabled = true;
    win.enableChunkedLoading = () => {
      win.chunkedLoadingEnabled = true;
      console.log('[ProjectsService] Chunked loading ENABLED (1 month at a time).');
    };
    win.disableChunkedLoading = () => {
      win.chunkedLoadingEnabled = false;
      console.log('[ProjectsService] Chunked loading DISABLED (loads the full date range in a single request).');
    };
  }

  async GetProjects(start_date, end_date, url_mod) {
    // Ensure start_date is at beginning of day (00:00:00) and end_date is at end of day (23:59:59)
    start_date = dayjs(start_date).format('YYYY-MM-DD') + ' 00:00:00';
    end_date = dayjs(end_date).format('YYYY-MM-DD') + ' 23:59:59';
    let project_data = [];
    let data: any = await this.apiService.get(`projects?start_date=${start_date}&end_date=${end_date}${url_mod}`);
    if (data.success && data.data) {
      for (const item of data.data) {
        if (item) {
          item.generated_id = item.project_scheduler_guid + this.globalService.nullToEmpty(item.company_guid) + this.globalService.nullToEmpty(item.client_guid) + dayjs(item.internal_start_date).format('YYYY-MM-DD') + "T00:00:00";
          if (!item.estimated_time && Array.isArray(item.tasks) && item.tasks.length > 0) {
            item.estimated_time = item.tasks.reduce((sum: number, t: any) => sum + (Number(t?.estimated_time) || 0), 0);
          }
          project_data.push(item);
        }
      }
    } else if (!data.success) {
      this.globalService.presentToastMessage(data.message, data.success);
      return;
    }

    let generated_projects = await this.GetFutureProjects(start_date, end_date, url_mod);
    if (generated_projects) {
      // Set instead of a linear project_data scan per item — avoids quadratic growth when
      // there are a lot of real + generated projects in the requested range.
      const existing_ids = new Set<string>(project_data.map((p: any) => p.generated_id));
      for (const item of generated_projects) {
        if (item) {
          item.generated_id = item.project_scheduler_guid + this.globalService.nullToEmpty(item.company_guid) + this.globalService.nullToEmpty(item.client_guid) + dayjs(item.internal_start_date).format('YYYY-MM-DD') + "T00:00:00";
          if (!existing_ids.has(item.generated_id)) {
            existing_ids.add(item.generated_id);
            project_data.push(item);
          }
        }
      }
    }
    
    const invalidGuids = new Set<string>();

    for (let i = project_data.length - 1; i >= 0; i--) {
      const project = project_data[i];
      const guid = project.client_guid || project.company_guid;

      if (invalidGuids.has(guid)) {
      project_data.splice(i, 1);
      continue;
      }

      if (project.client_guid) {
      project.client = await this.globalService.GetClient(project.client_guid);
      if (!project.client) {
        invalidGuids.add(project.client_guid);
        project_data.splice(i, 1);
        continue;
      }
      project.linked_name = project.client.display_name;
      } else if (project.company_guid) {
      project.company = await this.globalService.GetCompany(project.company_guid);
      if (!project.company) {
        
        invalidGuids.add(project.company_guid);
        project_data.splice(i, 1);
        continue;
      }
      project.linked_name = project.company.name;
      }
    }

    return project_data;
  }

  /**
   * Fetches the real, already-created projects for the whole date range in a single request
   * (this list is inherently bounded by what actually exists — it's not the source of the
   * memory blowup, so it doesn't need to be chunked).
   */
  async GetDbProjectsOnly(start_date: string, end_date: string, url_mod: string): Promise<any[]> {
    start_date = dayjs(start_date).format('YYYY-MM-DD') + ' 00:00:00';
    end_date = dayjs(end_date).format('YYYY-MM-DD') + ' 23:59:59';
    const project_data: any[] = [];
    const data: any = await this.apiService.get(`projects?start_date=${start_date}&end_date=${end_date}${url_mod}`);
    if (data.success && data.data) {
      for (const item of data.data) {
        if (item) {
          item.generated_id = item.project_scheduler_guid + this.globalService.nullToEmpty(item.company_guid) + this.globalService.nullToEmpty(item.client_guid) + dayjs(item.internal_start_date).format('YYYY-MM-DD') + 'T00:00:00';
          if (!item.estimated_time && Array.isArray(item.tasks) && item.tasks.length > 0) {
            item.estimated_time = item.tasks.reduce((sum: number, t: any) => sum + (Number(t?.estimated_time) || 0), 0);
          }
          project_data.push(item);
        }
      }
    } else if (!data.success) {
      this.globalService.presentToastMessage(data.message, data.success);
      return [];
    }
    const invalidGuids = new Set<string>();
    for (let i = project_data.length - 1; i >= 0; i--) {
      const project = project_data[i];
      const guid = project.client_guid || project.company_guid;
      if (invalidGuids.has(guid)) { project_data.splice(i, 1); continue; }
      if (project.client_guid) {
        project.client = await this.globalService.GetClient(project.client_guid);
        if (!project.client) { invalidGuids.add(project.client_guid); project_data.splice(i, 1); continue; }
        project.linked_name = project.client.display_name;
      } else if (project.company_guid) {
        project.company = await this.globalService.GetCompany(project.company_guid);
        if (!project.company) { invalidGuids.add(project.company_guid); project_data.splice(i, 1); continue; }
        project.linked_name = project.company.name;
      }
    }
    return project_data;
  }

  /**
   * Fetches the recurring-project schedule (repeat rules) once for the whole date range. This is
   * just the schedule definitions, not generated instances — generating instances from this data
   * is cheap, local, and is what GetProjectsChunked below chunks month by month.
   */
  async FetchScheduleData(end_date: string, url_mod: string): Promise<any[]> {
    url_mod = this.globalService.trimLeadingAmpersand(url_mod);
    const params = url_mod ? `${url_mod}&end_date=${end_date}` : `end_date=${end_date}`;
    const result: any = await this.apiService.GetProjectSchedule(params);
    return result?.data ?? [];
  }

  /** Resolves each unique client/company referenced by the schedule once (GetClient/GetCompany are cached anyway, but this avoids repeat lookups per occurrence per chunk). */
  async BuildEntityMap(scheduleData: any[]): Promise<Map<string, any>> {
    const map = new Map<string, any>();
    for (const item of scheduleData) {
      if (item.client_guid && !map.has(item.client_guid)) {
        const client = await this.globalService.GetClient(item.client_guid);
        if (client) map.set(item.client_guid, client);
      }
      if (item.company_guid && !map.has(item.company_guid)) {
        const company = await this.globalService.GetCompany(item.company_guid);
        if (company) map.set(item.company_guid, company);
      }
    }
    return map;
  }

  /** Generates recurring project instances for a narrow window from already-fetched schedule data — no API calls. */
  GetFutureProjectsFromCachedSchedule(scheduleData: any[], start_date: string, end_date: string, entityMap: Map<string, any>): any[] {
    start_date = dayjs(start_date).format('YYYY-MM-DD') + ' 00:00:00';
    end_date = dayjs(end_date).format('YYYY-MM-DD') + ' 23:59:59';
    const return_data: any[] = [];
    for (const item of scheduleData) {
      const client_guid = item.client_guid;
      const company_guid = item.company_guid;
      if (client_guid) {
        const client = entityMap.get(client_guid);
        if (!client) continue;
        this.CreateProjectEvent(return_data, item, start_date, end_date, client_guid, true, client);
      }
      if (company_guid) {
        const company = entityMap.get(company_guid);
        if (!company) continue;
        this.CreateProjectEvent(return_data, item, start_date, end_date, company_guid, false, company);
      }
    }
    return return_data;
  }

  /**
   * Loads projects with only two API calls total: one for the real, already-created projects
   * covering the whole date range, and one for the recurring-project schedule covering the whole
   * date range. By default (see RegisterChunkedLoadingDebugHooks), future project instances are
   * then generated from that schedule one calendar month at a time, entirely client-side, calling
   * onChunk after each month so callers can render results as they arrive instead of waiting for
   * the whole range. Call window.disableChunkedLoading() in the Chrome console to load/generate
   * the whole range in a single pass instead, for comparison while debugging.
   */
  async GetProjectsChunked(
    start_date: string,
    end_date: string,
    url_mod: string,
    onChunk: (chunk_projects: any[], meta: { chunk_start: string, chunk_end: string, is_first: boolean, is_last: boolean }) => void | Promise<void>,
    isCancelled?: () => boolean
  ): Promise<void> {
    const overall_start = dayjs(start_date).startOf('day');
    const overall_end = dayjs(end_date).endOf('day');

    const chunking_enabled = typeof window !== 'undefined' && (window as any).chunkedLoadingEnabled === true;

    if (!chunking_enabled) {
      if (isCancelled && isCancelled()) return;
      const all_projects = await this.GetProjects(overall_start.format('YYYY-MM-DD'), overall_end.format('YYYY-MM-DD'), url_mod) || [];
      if (isCancelled && isCancelled()) return;
      await onChunk(all_projects, {
        chunk_start: overall_start.format('YYYY-MM-DD'),
        chunk_end: overall_end.format('YYYY-MM-DD'),
        is_first: true,
        is_last: true
      });
      return;
    }

    // The two API calls for the whole range.
    const db_projects = await this.GetDbProjectsOnly(overall_start.format('YYYY-MM-DD'), overall_end.format('YYYY-MM-DD'), url_mod);
    if (isCancelled && isCancelled()) return;

    const schedule_data = await this.FetchScheduleData(overall_end.format('YYYY-MM-DD'), url_mod);
    if (isCancelled && isCancelled()) return;

    const entity_map = await this.BuildEntityMap(schedule_data);
    if (isCancelled && isCancelled()) return;

    // Everything below is local generation only — no further network calls.
    const db_pool = [...db_projects];
    let chunk_start = overall_start;
    let is_first = true;

    while (chunk_start.isBefore(overall_end) || chunk_start.isSame(overall_end)) {
      if (isCancelled && isCancelled()) return;

      let chunk_end = chunk_start.add(1, 'month').subtract(1, 'day').endOf('day');
      if (chunk_end.isAfter(overall_end)) chunk_end = overall_end;
      const is_last = !chunk_end.isBefore(overall_end);

      const generated_chunk = this.GetFutureProjectsFromCachedSchedule(
        schedule_data,
        chunk_start.format('YYYY-MM-DD'),
        chunk_end.format('YYYY-MM-DD'),
        entity_map
      );

      // Claim real projects that are due by this chunk out of the pool (each is claimed exactly
      // once; whatever's left gets flushed on the last chunk as a safety net so nothing is dropped).
      const due_db_projects: any[] = [];
      for (let i = db_pool.length - 1; i >= 0; i--) {
        const p = db_pool[i];
        if (is_last || !dayjs(p.internal_start_date).isAfter(chunk_end)) {
          due_db_projects.push(p);
          db_pool.splice(i, 1);
        }
      }

      const chunk_projects = [...due_db_projects];
      const chunk_ids = new Set<string>(chunk_projects.map((p: any) => p.generated_id));
      for (const item of generated_chunk) {
        if (item) {
          item.generated_id = item.project_scheduler_guid + this.globalService.nullToEmpty(item.company_guid) + this.globalService.nullToEmpty(item.client_guid) + dayjs(item.internal_start_date).format('YYYY-MM-DD') + 'T00:00:00';
          if (!chunk_ids.has(item.generated_id)) {
            chunk_ids.add(item.generated_id);
            chunk_projects.push(item);
          }
        }
      }

      await onChunk(chunk_projects, {
        chunk_start: chunk_start.format('YYYY-MM-DD'),
        chunk_end: chunk_end.format('YYYY-MM-DD'),
        is_first,
        is_last
      });
      is_first = false;

      if (is_last) break;

      // Everything above is synchronous local generation chained with Promise microtasks —
      // with no real network call per chunk anymore, the whole loop can otherwise run to
      // completion without the browser ever getting a chance to paint, so the "loading in the
      // background" UI would flip on and off before a single frame renders. Yielding to a
      // macrotask here forces an actual paint of what we just added before generating the next
      // chunk, which is what makes the progressive/background-loading UX visible at all.
      await new Promise<void>(resolve => setTimeout(resolve, 0));

      chunk_start = chunk_end.add(1, 'day').startOf('day');
    }
  }

  async GetFutureProjects(start_date, end_date, url_mod) {
    let performance_start = performance.now();
    let return_data = [];
    url_mod = this.globalService.trimLeadingAmpersand(url_mod);
    url_mod += "&end_date=" + end_date;
    let projects_schedule: any = await this.apiService.GetProjectSchedule(url_mod);
    projects_schedule = projects_schedule.data;
    let schedule_data = projects_schedule;
    if (!schedule_data) return null;
    for (var i = 0; i < schedule_data.length; i++) {
      var item = schedule_data[i];
      var client_guid = item.client_guid;
      var company_guid = item.company_guid;

      if (client_guid) {
        var client = await this.globalService.GetClient(client_guid);
        if (!client) {
          continue;
        }
        this.CreateProjectEvent(return_data, item, start_date, end_date, client_guid, true, client);
      }
      if (company_guid) {
        var company = await this.globalService.GetCompany(company_guid);
        if (!company) {
          continue;
        }
        this.CreateProjectEvent(return_data, item, start_date, end_date, company_guid, false, company);
      }

    }
    let performance_end = performance.now();
    return return_data;
  }


  async GetFutureTasks(start_date, end_date) {
    let return_data = []
    let project_data: any = await this.GetFutureProjects(start_date, end_date, "");
    if (project_data == null) return null;

    for (const project of project_data) {
      if (project.tasks) {
        let task_index = 0;
        for (const task of project.tasks) {
          if (task) {
            task.guid = project.guid + "TASK~" + task_index;
            task_index++;
            if (task.relative_date) {
              if (task.relative_to_start) {
                task.due_date = dayjs(project.internal_start_date).startOf('day').add(task.due_date_relative, 'day').format('YYYY-MM-DD');
              } else {
                task.due_date = dayjs(project.internal_end_date).startOf('day').add(task.due_date_relative, 'day').format('YYYY-MM-DD');
              }
            }
            ApplyUseProjectDates(task, project.internal_start_date, project.internal_end_date);

            let cli_guid = project.client_guid;
            if (!project.client_guid) cli_guid = project.company_guid;

            task.uuid = project.project_scheduler_guid + cli_guid + task.due_date + task.title;
            task.is_fake = true;
            task.project = JSON.parse(JSON.stringify(project));

            if (dayjs(task.due_date).startOf('day') > dayjs(end_date).startOf('day')) {
              continue;
            }

            if ((return_data.findIndex(item => item.uuid === task.uuid) == -1))
              return_data.push(task);
          }
        }
      }
    }
    return return_data;
  }


  CreateProjectEvent(return_data, item, start_date, end_date, cli_guid, is_client, comp_or_client) {


    // When generating chunk by chunk (one month at a time) across a wide date range, this gets
    // called many times for the same scheduler item. Cache the parsed template/rules on the
    // item itself so we don't re-parse the same JSON on every chunk; each occurrence below is
    // still deep-cloned before being pushed to return_data, so reusing/mutating these cached
    // objects across calls is safe.
    const project_template = item.project_templates[0];
    const project_json_data = project_template._parsed_json_data
      || (project_template._parsed_json_data = JSON.parse(project_template.json_str));
    const rules: RepeatRules = item._parsed_repeat_rules
      || (item._parsed_repeat_rules = JSON.parse(item.repeat_rules));

    // return_data is shared across every schedule item processed in this generation pass
    // (see GetFutureProjectsFromCachedSchedule/GetFutureProjects), so a linear
    // return_data.findIndex(...) dedup check here gets slower the more projects have
    // already been generated (quadratic overall). Track seen guids in a Set instead — the
    // set is stashed directly on the return_data array so every call sharing that array
    // shares the same set.
    const seen_project_guids: Set<string> = (return_data as any)._seen_project_guids
      || ((return_data as any)._seen_project_guids = new Set<string>(return_data.map((x: any) => x.guid)));
    // Anchor on the schedule's raw occurrence grid (rules.repeat_start_date), not
    // item.next_create_date - next_create_date is already offset-shifted (it's really "what
    // internal_start_date will be for the next occurrence", stored that way so the backend can use
    // it directly as a query gate). Anchoring buildOccurrences on it instead of the raw grid point
    // treats an already-shifted date as if it were on-grid, and since every found occurrence then
    // has the same offset added again below, every occurrence after the first lands a full offset
    // unit too late (e.g. Jan instead of Dec for a "+1 month" schedule) - matches the client-schedule
    // preview component, and the backend's own CreateProjectsFromSchedule, which both anchor on
    // rules.repeat_start_date directly.
    // Fall back to item.start_date (a legacy/separate column, mirroring the same fallback
    // CreateProjectsFromSchedule uses on the backend) and then item.next_create_date, in case an
    // older schedule's repeat_rules JSON is missing repeat_start_date - dayjs(undefined) resolves to
    // "now", which would silently anchor the grid wrong rather than erroring.
    const anchorISO = rules.repeat_start_date || item.next_create_date;    // the reference “first” date

    // original template dates (used to keep offset deltas)
    const orgRepeatStartDate: string = project_json_data.repeat_start_date;

    // Get offsets, units, and direction flags
    const intStartOffset: number = Number(rules.internal_start_offset) || 0;
    const intStartUnit: 'day' | 'week' | 'month' = rules.internal_start_offset_unit || 'day';
    const intStartBefore: boolean = rules.internal_start_date_before || false;

    const intEndOffset: number = Number(rules.internal_end_offset) || 0;
    const intEndUnit: 'day' | 'week' | 'month' = rules.internal_end_offset_unit || 'day';
    const intEndBefore: boolean = rules.internal_end_date_before || false;

    const extStartOffset: number = Number(rules.external_start_offset) || 0;
    const extStartUnit: 'day' | 'week' | 'month' = rules.external_start_offset_unit || 'day';
    const extStartBefore: boolean = rules.external_start_date_before || false;

    const extEndOffset: number = Number(rules.external_end_offset) || 0;
    const extEndUnit: 'day' | 'week' | 'month' = rules.external_end_offset_unit || 'day';
    const extEndBefore: boolean = rules.external_end_date_before || false;

    const periodStartOffset: number = Number(rules.period_start_offset) || 0;
    const periodStartUnit: 'day' | 'week' | 'month' = rules.period_start_offset_unit || 'day';
    const periodStartBefore: boolean = rules.period_start_date_before || false;

    const periodEndOffset: number = Number(rules.period_end_offset) || 0;
    const periodEndUnit: 'day' | 'week' | 'month' = rules.period_end_offset_unit || 'day';
    const periodEndBefore: boolean = rules.period_end_date_before || false;

    // Expand the occurrence search window to account for internal_start_date offsets that move
    // the occurrence anchor outside the requested [start_date, end_date] range.
    // - "before" offsets (anchor - N): the anchor is LATER than internal_start_date, e.g. an
    //   occurrence on July 31 with internal_start 2 months before yields internal_start=May 1,
    //   so when viewing May we need to find anchors up to 2 months after the view window end.
    // - "after" offsets (anchor + N, the default/most common case, e.g. "start 3 months after
    //   fiscal year end"): the anchor is EARLIER than internal_start_date, e.g. an anchor of
    //   Dec 31 with internal_start 3 months after yields internal_start=Mar 1 the following year,
    //   so when viewing that March we need to find anchors up to 3 months before the view window
    //   start. Without this, the anchor falls outside the search window entirely and no project
    //   is ever generated for it.
    let occ_start_date = start_date;
    let occ_end_date = end_date;
    if (intStartBefore && intStartOffset > 0) {
      occ_end_date = dayjs(end_date).add(intStartOffset + 1, intStartUnit).format('YYYY-MM-DD') + 'T23:59:59';
    } else if (!intStartBefore && intStartOffset > 0) {
      occ_start_date = dayjs(start_date).subtract(intStartOffset + 1, intStartUnit).format('YYYY-MM-DD');
    }
    const occurrences = this.buildOccurrences(rules, anchorISO, occ_start_date, occ_end_date);

    for (const occ of occurrences) {
      // occ is the "internal start anchor" analogue = what used to be next_date/start
      // All offsets are relative to repeat_start_date

      // The anchor for offsets is repeat_start_date, but we want the occurrence's year/month/day
      // So, align occ to repeat_start_date's time, then add offsets

      const baseDate = dayjs(orgRepeatStartDate)
        .year(occ.year()).month(occ.month()).date(occ.date());

      let internal_start_date = (intStartBefore
        ? baseDate.subtract(intStartOffset, intStartUnit)
        : baseDate.add(intStartOffset, intStartUnit)
      ).format('YYYY-MM-DD') + 'T00:00:00';
      let internal_end_date = (intEndBefore
        ? baseDate.subtract(intEndOffset, intEndUnit)
        : baseDate.add(intEndOffset, intEndUnit)
      ).format('YYYY-MM-DD') + 'T00:00:00';
      let external_start_date = (extStartBefore
        ? baseDate.subtract(extStartOffset, extStartUnit)
        : baseDate.add(extStartOffset, extStartUnit)
      ).format('YYYY-MM-DD') + 'T00:00:00';
      let external_end_date = (extEndBefore
        ? baseDate.subtract(extEndOffset, extEndUnit)
        : baseDate.add(extEndOffset, extEndUnit)
      ).format('YYYY-MM-DD') + 'T00:00:00';
      let period_start_date = (periodStartBefore
        ? baseDate.subtract(periodStartOffset, periodStartUnit)
        : baseDate.add(periodStartOffset, periodStartUnit)
      ).format('YYYY-MM-DD') + 'T00:00:00';
      let period_end_date = (periodEndBefore
        ? baseDate.subtract(periodEndOffset, periodEndUnit)
        : baseDate.add(periodEndOffset, periodEndUnit)
      ).format('YYYY-MM-DD') + 'T00:00:00';

      // Handle monthly offset_type adjustments
      if (rules.frequency === 'monthly' || rules.frequency === 'quarterly' || rules.frequency === 'semi-annually' || rules.frequency === 'yearly') {
        // Project Start Date
        if (rules.internal_start_offset_type === 'start_of_month') {
          internal_start_date = dayjs(internal_start_date).startOf('month').format("YYYY-MM-DD") + 'T00:00:00';
        } else if (rules.internal_start_offset_type === 'end_of_month') {
          internal_start_date = dayjs(internal_start_date).endOf('month').format("YYYY-MM-DD") + 'T00:00:00';
        }
        // else 'calculated_date' - use the computed date as-is

        // Project End Date
        if (rules.internal_end_offset_type === 'start_of_month') {
          internal_end_date = dayjs(internal_end_date).startOf('month').format("YYYY-MM-DD") + 'T00:00:00';
        } else if (rules.internal_end_offset_type === 'end_of_month') {
          internal_end_date = dayjs(internal_end_date).endOf('month').format("YYYY-MM-DD") + 'T00:00:00';
        }

        // External Start Date
        if (rules.external_start_offset_type === 'start_of_month') {
          external_start_date = dayjs(external_start_date).startOf('month').format("YYYY-MM-DD") + 'T00:00:00';
        } else if (rules.external_start_offset_type === 'end_of_month') {
          external_start_date = dayjs(external_start_date).endOf('month').format("YYYY-MM-DD") + 'T00:00:00';
        }

        // External End Date
        if (rules.external_end_offset_type === 'start_of_month') {
          external_end_date = dayjs(external_end_date).startOf('month').format("YYYY-MM-DD") + 'T00:00:00';
        } else if (rules.external_end_offset_type === 'end_of_month') {
          external_end_date = dayjs(external_end_date).endOf('month').format("YYYY-MM-DD") + 'T00:00:00';
        }

        // Period Start Date
        if (rules.period_start_offset_type === 'start_of_month') {
          period_start_date = dayjs(period_start_date).startOf('month').format("YYYY-MM-DD") + 'T00:00:00';
        } else if (rules.period_start_offset_type === 'end_of_month') {
          period_start_date = dayjs(period_start_date).endOf('month').format("YYYY-MM-DD") + 'T00:00:00';
        }

        // Period End Date
        if (rules.period_end_offset_type === 'start_of_month') {
          period_end_date = dayjs(period_end_date).startOf('month').format("YYYY-MM-DD") + 'T00:00:00';
        } else if (rules.period_end_offset_type === 'end_of_month') {
          period_end_date = dayjs(period_end_date).endOf('month').format("YYYY-MM-DD") + 'T00:00:00';
        }
      }

      // guard on window
      if (dayjs(internal_start_date).isBefore(dayjs(start_date)) ||
        dayjs(internal_start_date).isAfter(dayjs(end_date))) {
        
        continue;
      }

      // tasks (unchanged logic, but using the computed dates)
      const project_tasks = [];
      let task_index = 0;
      for (const task of project_json_data.tasks) {
        if (!task) continue;
        task.guid = item.guid + cli_guid + internal_start_date + 'TASK~' + task_index++;
        if (task.relative_date) {
          const is_date_relative = task.relative_date;

          if (is_date_relative) {
            const relative_to_start = task.relative_to_start;
            const start_date_relative = task.start_date_relative || 0;
            const start_date_relative_unit = task.start_date_relative_unit || 'day';
            const due_date_relative = task.due_date_relative || 0;
            const due_date_relative_unit = task.due_date_relative_unit || 'day';

            const base_date = relative_to_start ? internal_start_date : internal_end_date;

            if(relative_to_start)
            {
                task.start_date = dayjs(internal_start_date).add(start_date_relative, start_date_relative_unit).format('YYYY-MM-DD');
                task.due_date = dayjs(internal_start_date).add(due_date_relative, due_date_relative_unit).format('YYYY-MM-DD');
                
            }
            else
            {
                task.start_date = dayjs(internal_end_date).add(start_date_relative * -1, start_date_relative_unit).format('YYYY-MM-DD');
                task.due_date = dayjs(internal_end_date).add(due_date_relative * -1, due_date_relative_unit).format('YYYY-MM-DD');
            }
          }
          ApplyUseProjectDates(task, internal_start_date, internal_end_date);

          
          if (dayjs(task.due_date).isAfter(dayjs(internal_end_date))) {
            task.due_date = dayjs(internal_end_date).format('YYYY-MM-DD');
          }
          if (dayjs(task.start_date).isBefore(dayjs(internal_start_date))) {
            task.start_date = dayjs(internal_start_date).format('YYYY-MM-DD');
          }
        }
        task.uuid = item.guid + cli_guid + task.due_date + task.title;
        task.is_fake = true;
        //We need to make sure some default settings are applied
        if (task.assignment_settings == null) {
          task.assignment_settings = 0;
        }
        if (task.assigned_member_guid == null) {
          task.assigned_member_guid = '';
        }
        if (task.estimated_hours == null) {
          task.estimated_hours = 0;
        }
        if (task.estimated_mins == null) {
          task.estimated_mins = 0;
        }
        if (task.estimated_time == null) {
          task.estimated_time = 0;
        }

        // Was checking return_data (the projects array being built up here) for a matching
        // task uuid — return_data entries don't have a uuid field, so this never actually
        // deduped anything, and got more expensive the more projects had already been
        // generated. The real intent is to dedupe within this occurrence's own task list.
        if (project_tasks.findIndex(x => x.uuid === task.uuid) === -1) {
          project_tasks.push(task);
        }
      }

      const linked_name = is_client ? comp_or_client.display_name : comp_or_client.name;
      let title = project_json_data.title;
      if (project_json_data.add_numbers) {

      }
      let project_status = -1;
      if (dayjs().isAfter(dayjs(internal_start_date), 'day') || dayjs().isSame(dayjs(internal_start_date), 'day')) {
        project_status = 0;
      }
      const project = {
        created_date: occ.format('YYYY-MM-DD') + 'T00:00:00',
        description: project_json_data.description,
        external_end_date: external_end_date,
        external_start_date: external_start_date,
        guid: item.guid + cli_guid + internal_start_date,
        internal_end_date: internal_end_date,
        internal_start_date: internal_start_date,
        progress: 0,
        status: project_status,
        title: title,
        client_guid: is_client ? cli_guid : null,
        company_guid: is_client ? null : cli_guid,
        client: is_client ? { guid: cli_guid } : null,
        company: is_client ? null : { guid: cli_guid },
        is_fake: true,
        project_scheduler_guid: item.guid,
        force_order: project_json_data.force_order,
        tasks: project_tasks,
        default_billing_group: project_json_data.default_billing_group,
        auto_assign_team_members: project_json_data.auto_assign_team_members,
        linked_name,
        tags: project_json_data.tags,
        groups: project_json_data.groups,
        row_colour: project_json_data.row_colour,
        internal_status: project_json_data.internal_status,
        team_leader: project_json_data.team_leader,
        paused: item.paused,
        period_end_date: period_end_date,
        period_start_date: period_start_date,
        all_team_members: project_json_data.all_team_members,
        team_members: project_json_data.team_members,
        role_guid: project_json_data.role_guid,
        assignment_settings: project_json_data.assignment_settings,
        estimated_time: project_json_data.estimated_time ?? 0,
        estimated_time_remaining: project_json_data.estimated_time_remaining ?? 0
      };


      if (!seen_project_guids.has(project.guid)) {
        if (
          dayjs(internal_start_date).isAfter(dayjs(item.next_create_date), 'day') ||
          dayjs(internal_start_date).isSame(dayjs(item.next_create_date), 'day')
        ) {
          return_data.push(JSON.parse(JSON.stringify(project)));
          seen_project_guids.add(project.guid);
        }
        else {
          
        }
      }
    }
  }

  buildOccurrences(
    rules: RepeatRules,
    anchorISO: string,          // the “start” reference, e.g. item.next_create_date (ISO)
    windowStartISO: string,     // start_date (ISO)
    windowEndISO: string        // end_date (ISO)
  ): dayjs.Dayjs[] {
    const occurrences: dayjs.Dayjs[] = [];
    const anchor = dayjs(anchorISO).startOf('day');
    const winStart = dayjs(windowStartISO).startOf('day');
    const winEnd = dayjs(windowEndISO).startOf('day');
    const interval = Math.max(1, rules.interval || 1);

    // termination controls
    const endType = rules.end_type || 'never';
    const endOnDate = rules.end_date ? dayjs(rules.end_date).startOf('day') : null;
    const endAfter = Math.max(1, rules.end_after || 1);

    let count = 0;

    const pushIfInWindow = (d: dayjs.Dayjs) => {
      if (d.isBefore(winStart) || d.isAfter(winEnd)) return false;
      occurrences.push(d);
      count++;
      return true;
    };

    const hitEnd = (candidate: dayjs.Dayjs) => {
      if (endType === 'on' && endOnDate && candidate.isAfter(endOnDate)) return true;
      if (endType === 'after' && count >= endAfter) return true;
      if (candidate.isAfter(winEnd)) return true;
      return false;
    };

    if (rules.frequency == 'quarterly' || rules.frequency == 'semi-annually') rules.frequency = 'monthly';

    if (rules.frequency === 'daily') {
      // Every N days from anchor
      let d = anchor;
      // bring d up to windowStart (step by interval)
      if (d.isBefore(winStart)) {
        const diff = winStart.diff(d, 'day');
        const steps = Math.floor(diff / interval);
        d = d.add(steps * interval, 'day');
        if (d.isBefore(winStart)) d = d.add(interval, 'day');
      }
      while (!hitEnd(d)) {
        if (d.isAfter(winEnd)) break;
        pushIfInWindow(d);
        d = d.add(interval, 'day');
      }
    } else if (rules.frequency === 'weekly') {
      // weekly on selected weekdays; interval is number of weeks
      // Assumption: rules.weekdays[0] = Sunday, ... [6] = Saturday
      const picks = (rules.weekdays || []).map((v, i) => v ? i : -1).filter(i => i >= 0);
      if (picks.length === 0) picks.push(anchor.day()); // fallback: anchor’s weekday

      // Move to the start of the week containing anchor (Sunday)
      let weekStart = anchor.startOf('week'); // Sunday
      // fast-forward to the last cycle-aligned week that starts on or before winStart
      // so the inner loop's pushIfInWindow handles filtering individual days
      if (weekStart.isBefore(winStart)) {
        const diffWeeks = winStart.diff(weekStart, 'week');
        const steps = Math.floor(diffWeeks / interval);
        weekStart = weekStart.add(steps * interval, 'week');
      }

      // iterate weeks
      while (true) {
        for (const w of picks) {
          const d = weekStart.add(w, 'day');
          if (hitEnd(d)) return occurrences;
          pushIfInWindow(d);
        }
        weekStart = weekStart.add(interval, 'week');
        if (weekStart.isAfter(winEnd)) {
          break;
        }
      }
    } else if (rules.frequency === 'monthly') {
      // monthly on either a specific day-of-month OR nth weekday
      let d = anchor.startOf('month');
      // fast-forward months to the first candidate after windowStart - 1 day
      if (d.isBefore(winStart)) {
        const diffM = winStart.diff(d, 'month');
        const steps = Math.floor(diffM / interval);
        d = d.add(steps * interval, 'month');
      }

      while (true) {
        let candidate: dayjs.Dayjs | null = null;
        const y = d.year();
        const m = d.month() // dayjs month is 0-based, so +1 for 1-12

        let isMonthAllowed = true;
        if (rules.monthly_any === false) {
          // Only allow months that are checked in the months array
          const allowedMonths = rules.months || [];
          isMonthAllowed = allowedMonths.length === 0 || allowedMonths[m]; // m-1 because array is 0-based
        }
        // If monthly_any is true, we just repeat every X months (interval), so isMonthAllowed is always true

        if (isMonthAllowed) {
          if (rules.monthly_type === 'day') {
            const day = Math.max(1, rules.monthly_day || anchor.date());
            candidate = this.safeDate(y, m + 1, day);
          } else if (rules.monthly_type === 'special') {
            if (rules.monthly_special === 'start') {
              candidate = dayjs(new Date(y, m, 1));
            } else if (rules.monthly_special === 'end') {
              candidate = dayjs(new Date(y, m + 1, 0));
            }
          } else {
            const ord = this.parseOrdinal(rules.monthly_ordinal || '1');
            const wd = parseInt(rules.monthly_weekday ?? String(anchor.day()), 10);
            candidate = this.nthWeekdayOfMonth(y, m + 1, wd, ord);
          }

          if (candidate) {
            if (hitEnd(candidate)) break;
            pushIfInWindow(candidate);
          }
        }

        d = d.add(interval, 'month');
        if (d.isAfter(winEnd.endOf('month'))) {
          break;
        }
      }
    }
    else if (rules.frequency == 'semi-monthly') {
      //We need the logic to handle semi monthly. It creates two dates based on the semi_monthly_second_day semi_monthly_first_day
      let d = anchor.startOf('month');
      // fast-forward months to the first candidate after windowStart - 1 day
      if (d.isBefore(winStart)) {
        const diffM = winStart.diff(d, 'month');
        const steps = Math.floor(diffM / interval);
        d = d.add(steps * interval, 'month');
      }

      while (true) {
        const y = d.year();
        const m = d.month() + 1;

        // First day
        if (rules.semi_monthly_first_day) {
          const firstCandidate = this.safeDate(y, m, rules.semi_monthly_first_day);
          if (!hitEnd(firstCandidate)) {
            pushIfInWindow(firstCandidate);
          } else {
            break;
          }
        }

        // Second day (only if different from first)
        if (
          rules.semi_monthly_second_day &&
          rules.semi_monthly_second_day !== rules.semi_monthly_first_day
        ) {
          const secondCandidate = this.safeDate(y, m, rules.semi_monthly_second_day);
          if (!hitEnd(secondCandidate)) {
            pushIfInWindow(secondCandidate);
          } else {
            break;
          }
        }

        d = d.add(interval, 'month');
        if (d.isAfter(winEnd.endOf('month'))) break;
      }
    }
    else if (rules.frequency === 'yearly') {
      // yearly on a fixed date or ordinal weekday in a month
      let d = anchor.startOf('year');
      if (d.isBefore(winStart)) {
        const diffY = winStart.diff(d, 'year');
        const steps = Math.floor(diffY / interval);
        d = d.add(steps * interval, 'year');
      }

      while (true) {
        let candidate: dayjs.Dayjs | null = null;
        const y = d.year();

        if (rules.yearly_use_ordinal) {
          const ord = this.parseOrdinal(rules.yearly_ordinal || '1');
          const wd = parseInt(rules.yearly_weekday ?? String(anchor.day()), 10);
          const m = Math.max(1, rules.yearly_ordinal_month || (anchor.month()));
          candidate = this.nthWeekdayOfMonth(y, m, wd, ord);
        } else {
          const m = Math.max(1, rules.yearly_month || (anchor.month()));
          const day = Math.max(1, rules.yearly_day || anchor.date());
          candidate = this.safeDate(y, m, day);
        }

        if (candidate) {
          if (hitEnd(candidate)) break;
          pushIfInWindow(candidate);
        }

        d = d.add(interval, 'year');
        if (d.isAfter(winEnd.endOf('year'))) break;
      }
    }

    return occurrences.sort((a, b) => a.valueOf() - b.valueOf());
  }
  /** Convert ordinal string to a number; 'last' => -1 */
  parseOrdinal(s: string): number {
    if (!s) return 1;
    if (s.toLowerCase() === 'last') return -1;
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : 1;
  }

  /** nth weekday of a given month (month is 1..12); ord=-1 for last */
  nthWeekdayOfMonth(year: number, month1: number, weekday: number, ord: number): dayjs.Dayjs | null {
    const month0 = month1 - 1;
    // Must be time-normalized (midnight), not dayjs() "now" - otherwise a candidate landing on
    // the same calendar day as a window boundary (which IS normalized via .startOf('day')
    // elsewhere in buildOccurrences) can carry a nonzero time-of-day and compare as "after" a
    // same-day windowEnd purely because of what time it happens to be when this code runs.
    let d = dayjs().startOf('day').year(year).month(month0).date(1);
    const daysInMonth = d.daysInMonth();

    if (ord <= 4) {
      // find first weekday in month
      const firstWeekdayOffset = (weekday - d.day() + 7) % 7;
      const date = 1 + firstWeekdayOffset + (ord - 1) * 7;
      if (date > daysInMonth) return null;
      return d.date(date);
    }

    // last weekday
    let last = d.date(daysInMonth);
    const backOffset = (last.day() - weekday + 7) % 7;
    last = last.subtract(backOffset, 'day');
    return last;
  }

  /** Safely clamp a day-of-month to the month’s length */
  safeDate(year: number, month1: number, day: number): dayjs.Dayjs {
    // Must be time-normalized (midnight) for the same reason as nthWeekdayOfMonth above -
    // dayjs() "now" carries the real wall-clock time-of-day, which corrupts isAfter/isBefore
    // comparisons against window boundaries that land on the same calendar day.
    const dt = dayjs().startOf('day').year(year).month(month1 - 1).date(1);
    const max = dt.daysInMonth();
    const clamped = Math.min(day, max);
    return dt.date(clamped);
  }

  GetNextValidDateFromToday(repeat_rules: RepeatRules): string {
    if (repeat_rules.relative_date_type !== 'project_repeat_date') return dayjs().format('YYYY-MM-DD');
    if (repeat_rules.frequency === 'weekly') {
      const hasWeekday = Array.isArray(repeat_rules.weekdays) && repeat_rules.weekdays.some(w => w);
      if (!hasWeekday) return dayjs().format('YYYY-MM-DD');
    }
    const tempRules = { ...repeat_rules, repeat_start_date: dayjs().format('YYYY-MM-DD') };
    return this.FixStartDate(false, tempRules) ?? dayjs().format('YYYY-MM-DD');
  }

  FixStartDate(update_date: boolean, repeat_rules: RepeatRules): string | null {
    if (repeat_rules.relative_date_type == 'year_end_date') return null;

    let current_start_date = dayjs(repeat_rules.repeat_start_date);

    if (repeat_rules.frequency === 'weekly') {
      for (let i = 0; i < 7; i++) {
        const weekday = current_start_date.day();
        if (repeat_rules.weekdays[weekday]) {
          break;
        }
        current_start_date = current_start_date.add(1, 'day');
      }
    } else if (repeat_rules.frequency === 'monthly' || repeat_rules.frequency === 'quarterly' || repeat_rules.frequency === 'semi-annually') {

      if (repeat_rules.monthly_any) {
        // No specific month restriction
      }
      else {
        for (let i = 0; i < 12; i++) {
          if (repeat_rules.months[current_start_date.month()]) {
            break;
          }
          current_start_date = current_start_date.add(1, 'month');
        }
      }

      if (repeat_rules.monthly_type == 'day') {
        current_start_date = current_start_date.date(repeat_rules.monthly_day);
        if(current_start_date.date() < dayjs(repeat_rules.repeat_start_date).date()) {
          current_start_date = current_start_date.add(1, 'month').date(repeat_rules.monthly_day);
        } 
      } else if (repeat_rules.monthly_type == 'weekday') {
        // Calculate the date for the Nth weekday of the month
        const month = current_start_date.month();
        const year = current_start_date.year();
        const weekday = Number(repeat_rules.monthly_weekday); // 0 (Sun) - 6 (Sat)
        let ordinal = repeat_rules.monthly_ordinal;

        let targetDate: dayjs.Dayjs;

        if (ordinal === '5') {
          // Last weekday of the month
          targetDate = dayjs(new Date(year, month + 1, 0)); // Last day of month
          while (targetDate.day() !== weekday) {
            targetDate = targetDate.subtract(1, 'day');
          }
        } else {
          // Nth weekday of the month
          targetDate = dayjs(new Date(year, month, 1));
          let count = 0;
          while (targetDate.month() === month) {
            if (targetDate.day() === weekday) {
              count++;
              if (count === Number(ordinal)) break;
            }
            targetDate = targetDate.add(1, 'day');
          }
          // If not found, fallback to first weekday
          if (targetDate.month() !== month) {
            targetDate = dayjs(new Date(year, month, 1));
            while (targetDate.day() !== weekday && targetDate.month() === month) {
              targetDate = targetDate.add(1, 'day');
            }
          }
        }
        current_start_date = targetDate;
        if(current_start_date.date() < dayjs(repeat_rules.repeat_start_date).date()) {
        current_start_date = current_start_date.add(1, 'month');
        // Recalculate the Nth weekday for the new month
        const newMonth = current_start_date.month();
        const newYear = current_start_date.year();
        const weekday = Number(repeat_rules.monthly_weekday);
        let ordinal = repeat_rules.monthly_ordinal;

        let targetDate: dayjs.Dayjs;

        if (ordinal === '5') {
          // Last weekday of the month
          targetDate = dayjs(new Date(newYear, newMonth + 1, 0));
          while (targetDate.day() !== weekday) {
            targetDate = targetDate.subtract(1, 'day');
          }
        } else {
          // Nth weekday of the month
          targetDate = dayjs(new Date(newYear, newMonth, 1));
          let count = 0;
          while (targetDate.month() === newMonth) {
            if (targetDate.day() === weekday) {
              count++;
              if (count === Number(ordinal)) break;
            }
            targetDate = targetDate.add(1, 'day');
          }
          if (targetDate.month() !== newMonth) {
            targetDate = dayjs(new Date(newYear, newMonth, 1));
            while (targetDate.day() !== weekday && targetDate.month() === newMonth) {
              targetDate = targetDate.add(1, 'day');
            }
          }
        }
        current_start_date = targetDate;
        }
      } else if (repeat_rules.monthly_type == 'special') {
        if (repeat_rules.monthly_special === 'start') {
          current_start_date = current_start_date.date(1);
        } else if (repeat_rules.monthly_special === 'end') {
          current_start_date = current_start_date.endOf('month');
        }
      }

    } else if (repeat_rules.frequency === 'semi-monthly') {
      // Ensure current_start_date matches the first semi-monthly day
      const firstDay = repeat_rules.semi_monthly_first_day;
      if (current_start_date.date() !== firstDay) {
        current_start_date = current_start_date.date(firstDay);
      }
    }
    else if (repeat_rules.frequency === 'yearly') {

      if (repeat_rules.yearly_use_ordinal) {
        // Check if current_start_date matches the specified ordinal, weekday, and month
        const ordinal = Number(repeat_rules.yearly_ordinal);
        const weekday = Number(repeat_rules.yearly_weekday); // 0 (Sun) - 6 (Sat)
        const ordinalMonth = Number(repeat_rules.yearly_ordinal_month) - 1; // 0-indexed

        // If current_start_date does not match, set it to the correct date
        if (
          current_start_date.month() !== ordinalMonth ||
          current_start_date.day() !== weekday ||
          (() => {
            // Calculate the ordinal of current_start_date in its month
            const year = current_start_date.year();
            const month = current_start_date.month();
            let d = dayjs(new Date(year, month, 1));
            let count = 0;
            let foundOrdinal = 0;
            while (d.month() === month) {
              if (d.day() === weekday) {
                count++;
                if (d.date() === current_start_date.date()) {
                  foundOrdinal = count;
                  break;
                }
              }
              d = d.add(1, 'day');
            }
            // If it's the last occurrence, treat as ordinal 5
            let lastOrdinal = 0;
            d = dayjs(new Date(year, month, 1));
            while (d.month() === month) {
              if (d.day() === weekday) {
                lastOrdinal = d.date();
              }
              d = d.add(1, 'day');
            }
            if (current_start_date.date() === lastOrdinal) {
              foundOrdinal = 5;
            }
            return foundOrdinal !== ordinal;
          })()
        ) {
          // Find the correct date for the given ordinal, weekday, and month
          const year = current_start_date.year();
          let targetDate: dayjs.Dayjs;
          if (ordinal === 5) {
            // Last weekday of the month
            targetDate = dayjs(new Date(year, ordinalMonth + 1, 0)); // Last day of month
            while (targetDate.day() !== weekday) {
              targetDate = targetDate.subtract(1, 'day');
            }
          } else {
            // Nth weekday of the month
            targetDate = dayjs(new Date(year, ordinalMonth, 1));
            let count = 0;
            while (targetDate.month() === ordinalMonth) {
              if (targetDate.day() === weekday) {
                count++;
                if (count === ordinal) break;
              }
              targetDate = targetDate.add(1, 'day');
            }
            // Fallback to first weekday if not found
            if (targetDate.month() !== ordinalMonth) {
              targetDate = dayjs(new Date(year, ordinalMonth, 1));
              while (targetDate.day() !== weekday && targetDate.month() === ordinalMonth) {
                targetDate = targetDate.add(1, 'day');
              }
            }
          }
          current_start_date = targetDate;
        }
      }
      else {
        if (
          current_start_date.month() !== (repeat_rules.yearly_month - 1) ||
          current_start_date.date() !== repeat_rules.yearly_day
        ) {
          current_start_date = current_start_date
            .month(repeat_rules.yearly_month - 1)
            .date(repeat_rules.yearly_day);
        }
      }
    }

    if (update_date) {
      repeat_rules.repeat_start_date = current_start_date.format('YYYY-MM-DD');
    }

    if (repeat_rules.repeat_start_date == current_start_date.format('YYYY-MM-DD')) return null;

    return current_start_date.format('YYYY-MM-DD');
  }

  async ConvertToCalendar(start_date, end_date, url_mod, type, events, show_projects, show_tasks, show_events) {
    start_date = dayjs(start_date).startOf('day').add(-14, 'day').toISOString();
    end_date = dayjs(end_date).startOf('day').add(14, 'day').toISOString();
    let future_projects = null;
    if (show_projects) {

      future_projects = await this.GetProjects(start_date, end_date, url_mod);
      for (const event of future_projects) {
        var eventExists = false;
        for (const existingEvent of events.data) {
          if (existingEvent.id === event.guid) {
            eventExists = true;
            break;
          }
        }
        if (eventExists) continue;
        let display_name = "";
        if (type != "client" || type != "company") {
          if (event.client) {
            display_name = " | " + event.client.display_name
          }
          else if (event.company) {
            display_name = " | " + event.company.display_name;
          }
        }

        let formatted_event = {
          id: event.guid,
          title: event.title,
          start: event.internal_end_date,
          color: '#3A67D6',
          allDay: true,
          extendedProps: { type: 'project' }
        }
        if (!eventExists) events.data.push(formatted_event);
      }
    }
    if (show_tasks) {
      if (future_projects == null) future_projects = await this.GetProjects(start_date, end_date, url_mod);
      for (const project of future_projects) {
        for (const task of project.tasks) {
          var eventExists = false;
          for (const existingEvent of events.data) {
            if (existingEvent.id === task.guid) {
              eventExists = true;
              break;
            }
          }

          // var eventExists = events.data.some(function (existingEvent) {
          //   return existingEvent.id === task.guid;
          // });
          if (eventExists) continue;
          let display_name = "";
          if (type != "client" || type != "company") {
            if (task.client_guid) {
              let client: any = await this.globalService.GetClient(task.client_guid);
              display_name = " | " + client.display_name
            }
            else if (task.company_guid) {
              let company: any = await this.globalService.GetCompany(task.company_guid);
              display_name = " | " + company.display_name;
            }
          }
          let formatted_event = {
            id: task.guid,
            title: task.title,
            start: task.due_date,
            color: '#70A9FF',
            allDay: true,
            extendedProps: { type: 'task' }
          }
          if (!eventExists) events.data.push(formatted_event);
        }
      }
    }
    if (show_events) {
      let future_calendar_events = null;
      future_calendar_events = await this.globalService.GetCalendarEvents(start_date, end_date, url_mod);
      for (const event of future_calendar_events) {
        var eventExists = false;
        for (const existingEvent of events.data) {
          if (existingEvent.id === event.guid) {
            eventExists = true;
            break;
          }
        }
        if (eventExists) continue;

        //Figure out the colours
        var start_formated = event.start_time;
        var end_formatted = event.end_time;
        if (start_formated.endsWith("Z")) {
          start_formated = start_formated.slice(0, -1); // Removes the last character
        }

        if (end_formatted.endsWith("Z")) {
          end_formatted = end_formatted.slice(0, -1); // Removes the last character
        }

        let formatted_event = {
          id: event.guid,
          title: event.title,
          start: start_formated + "Z",
          end: event.end_time + "Z",
          color: '#50C878',
          allDay: false
        }
        if (!eventExists) events.data.push(formatted_event);
      }
    }
    return;
  }

  ProjectHasExternalSteps(project) {
    for (const task of project.tasks) {
    for (const step of task.steps) {
      if (ClientStepTypes.includes(Number(step.type))) {
        return true;
      }
    }
  }
    return false;
  }

  TaskHasExternalSteps(task) {
    for (const step of task.steps) {
      if (ClientStepTypes.includes(Number(step.type))) {
        return true;
      }
    }
    return false;
  }

}
